import { useParams, useSearchParams } from 'react-router-dom';
import { SearchParam } from '@common/ts/enums';
import {
  ArrowLeft,
  AlertTriangle,
  BarChart3,
  Layers,
  ArrowLeftRight,
  FileText,
  HelpCircle,
  Users,
  Download,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import AnalysisSection from '@/components/analysis/AnalysisSection';
import InsightCardList from '@/components/analysis/InsightCardList';
import CopyButton from '@/components/ui/CopyButton';
import useTranslation from '@/hooks/useTranslation';
import { formatSectionsForClipboard, formatSummaryForClipboard } from '@/utils/formatForClipboard';
import { formatComparisonAsMarkdown, buildFullReportText } from '@/utils/formatComparisonExport';
import TextWithClickableQuotes from '@/utils/TextWithClickableQuotes';
import { resolveTranslation } from '@/utils/resolveTranslation';
import {
  translateStatisticalComparison,
  translateSampleProfiles,
  translateSharedThemes,
  translatePerQuestionComparison,
  translateComparisonSummary,
  translateKeyDifferences,
  translateSharedPatterns,
  translateUniqueToSegment,
  translateComparisonMetricKey,
} from '@/pages/projects/single-project/Content/project_results/resultsPageTranslations';
import { useGetComparison, ComparisonResult } from '@/reactQuery/comparison';
import ComparisonCategoryChart from '@/components/projects/single-project/comparison/ComparisonCategoryChart';

// Segment color scheme — Tellet brand: emerald (green) + pink
const SEGMENT_A_COLOR = 'emerald';
const SEGMENT_B_COLOR = 'pink';

interface ComparisonReportProps {
  comparisonId: string;
  analysisLanguage: string;
}

function formatDelta(delta: string): { text: string; color: string } {
  const num = parseInt(delta, 10);
  if (num > 0) return { text: `+${num}`, color: 'text-emerald-600' };
  if (num < 0) return { text: `${num}`, color: 'text-red-500' };
  return { text: '—', color: 'text-neutral-400' };
}

function renderProfileBars(profile: any, color: string) {
  const barColor = color === 'emerald' ? 'bg-emerald-400' : 'bg-pink-300';

  // Handle both formats: { demographics: [{ field, distribution }] } and Record<string, Array>
  const entries: Array<{
    field: string;
    items: Array<{ value: string; count: number; percentage: number }>;
  }> = [];

  if (Array.isArray(profile?.demographics)) {
    for (const d of profile.demographics) {
      if (d.field && Array.isArray(d.distribution)) {
        entries.push({ field: d.field, items: d.distribution });
      }
    }
  } else if (typeof profile === 'object' && profile !== null) {
    for (const [field, items] of Object.entries(profile)) {
      if (Array.isArray(items)) {
        entries.push({ field, items: items as any });
      }
    }
  }

  if (entries.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      {entries.slice(0, 4).map(({ field, items }) => (
        <div key={field}>
          <p className="text-xs font-medium text-neutral-500 mb-1">{field}</p>
          {items.slice(0, 5).map((item) => (
            <div key={item.value} className="flex items-center gap-2 mb-1">
              <div className="h-2 w-24 bg-neutral-100 rounded-full overflow-hidden">
                <div
                  className={`h-full ${barColor} rounded-full`}
                  style={{ width: `${item.percentage}%` }}
                />
              </div>
              <span className="text-xs text-neutral-600 truncate max-w-[100px]">{item.value}</span>
              <span className="text-xs text-neutral-400 tabular-nums">{item.count}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function ComparisonReportContent({
  data,
  analysisLanguage,
  filterIdA,
  filterIdB,
}: {
  data: ComparisonResult;
  analysisLanguage: string;
  filterIdA?: string;
  filterIdB?: string;
}) {
  const { lang } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const hasUniqueSection = data.unique_to_a.length > 0 || data.unique_to_b.length > 0;

  const handleBack = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(SearchParam.COMPARISON_ID);
      return next;
    });
  };

  const handleDownload = () => {
    const markdown = formatComparisonAsMarkdown(data);
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    const date = new Date().toISOString().split('T')[0];
    anchor.href = url;
    anchor.download = `comparison-${data.filter_a_name}-vs-${data.filter_b_name}-${date}.md`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const fullReportText = buildFullReportText(data);

  // Extract sample sizes from statistical comparison
  const completedRow = data.statistical_comparison.find((r) => r.metric === 'Completed Interviews');
  const sampleA = completedRow?.filter_a_value ?? 0;
  const sampleB = completedRow?.filter_b_value ?? 0;
  const totalSample = sampleA + sampleB;

  return (
    <div className="max-w-4xl mx-auto py-8 px-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="gap-1.5 text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeft className="h-4 w-4" />
          {lang.get('msg.backToComparisons')}
        </Button>

        <div className="flex items-center gap-2">
          {fullReportText && <CopyButton text={fullReportText} />}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleDownload}
            className="gap-1.5"
          >
            <Download className="h-4 w-4" />
            {lang.get('msg.download')}
          </Button>
        </div>
      </div>

      {/* Title + Sample Size Hero */}
      <div className="rounded-xl border border-neutral-200 bg-white p-6 mb-6">
        <h1 className="text-lg font-semibold text-neutral-900 text-center mb-4">
          "{data.filter_a_name}" vs "{data.filter_b_name}"
        </h1>
        {completedRow && (
          <div className="flex items-center justify-center gap-8">
            <div className="flex flex-col items-center gap-1 px-6 py-3 rounded-lg bg-emerald-50 border border-emerald-100">
              <span className="text-2xl font-bold text-emerald-700 tabular-nums">
                {sampleA}
                {totalSample > 0 ? ` of ${totalSample}` : ''}
              </span>
              <span className="text-xs text-emerald-600 font-medium">{lang.get('msg.respondents')}</span>
            </div>
            <span className="text-neutral-300 text-lg font-light">vs</span>
            <div className="flex flex-col items-center gap-1 px-6 py-3 rounded-lg bg-pink-50 border border-pink-100">
              <span className="text-2xl font-bold text-pink-600 tabular-nums">
                {sampleB}
                {totalSample > 0 ? ` of ${totalSample}` : ''}
              </span>
              <span className="text-xs text-pink-500 font-medium">
                {lang.get('msg.respondents')}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-5">
        {/* Executive Summary (Narrative) — narrative first */}
        {data.narrative && (
          <AnalysisSection
            title={resolveTranslation(translateComparisonSummary, analysisLanguage)}
            icon={<FileText className="h-4 w-4" />}
            copyText={formatSummaryForClipboard(data.narrative)}
            defaultOpen
            fullWidth
          >
            <div className="text-sm leading-relaxed text-neutral-700">
              <TextWithClickableQuotes text={data.narrative} />
            </div>
          </AnalysisSection>
        )}

        {/* Statistical Comparison */}
        {data.statistical_comparison.length > 0 && (
          <AnalysisSection
            title={resolveTranslation(translateStatisticalComparison, analysisLanguage)}
            icon={<BarChart3 className="h-4 w-4" />}
            defaultOpen
            fullWidth
          >
            <Card className="border-neutral-200/80 shadow-none overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200">
                    <th className="text-left py-2.5 px-4 text-xs font-medium text-neutral-500 uppercase tracking-wide">
                      {lang.get('msg.metric')}
                    </th>
                    <th className="text-right py-2.5 px-4 text-xs font-medium text-emerald-600 uppercase tracking-wide">
                      {data.filter_a_name}
                    </th>
                    <th className="text-right py-2.5 px-4 text-xs font-medium text-pink-500 uppercase tracking-wide">
                      {data.filter_b_name}
                    </th>
                    <th className="text-right py-2.5 px-4 text-xs font-medium text-neutral-500 uppercase tracking-wide">
                      {lang.get('msg.delta')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.statistical_comparison.map((row, i) => {
                    const delta = formatDelta(row.delta);
                    // Prefer the localized label resolved from metric_key.
                    // Comparisons persisted before metric_key existed only
                    // have the English `metric` field — fall back to that.
                    const metricLabel = row.metric_key
                      ? translateComparisonMetricKey(row.metric_key, analysisLanguage) || row.metric
                      : row.metric;
                    return (
                      <tr key={i} className={i % 2 === 1 ? 'bg-neutral-50' : 'bg-white'}>
                        <td className="py-2.5 px-4 text-sm text-neutral-700">{metricLabel}</td>
                        <td className="py-2.5 px-4 text-sm text-right text-neutral-900 tabular-nums">
                          {row.filter_a_value}
                        </td>
                        <td className="py-2.5 px-4 text-sm text-right text-neutral-900 tabular-nums">
                          {row.filter_b_value}
                        </td>
                        <td
                          className={`py-2.5 px-4 text-sm text-right font-medium tabular-nums ${delta.color}`}
                        >
                          {delta.text}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          </AnalysisSection>
        )}

        {/* Sample Profiles */}
        {(data.sample_profile_a || data.sample_profile_b) && (
          <AnalysisSection
            title={resolveTranslation(translateSampleProfiles, analysisLanguage)}
            icon={<Users className="h-5 w-5" />}
            defaultOpen
            fullWidth
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-semibold text-emerald-700 mb-3">
                  {data.filter_a_name}
                </h4>
                {data.sample_profile_a && renderProfileBars(data.sample_profile_a, SEGMENT_A_COLOR)}
              </div>
              <div>
                <h4 className="text-sm font-semibold text-pink-600 mb-3">{data.filter_b_name}</h4>
                {data.sample_profile_b && renderProfileBars(data.sample_profile_b, SEGMENT_B_COLOR)}
              </div>
            </div>
          </AnalysisSection>
        )}

        {/* Shared Themes */}
        {data.shared_themes.length > 0 && (
          <AnalysisSection
            title={resolveTranslation(translateSharedThemes, analysisLanguage)}
            icon={<Layers className="h-4 w-4" />}
            copyText={formatSectionsForClipboard(data.shared_themes)}
            defaultOpen
            fullWidth
          >
            <InsightCardList data={data.shared_themes} />
          </AnalysisSection>
        )}

        {/* Unique to A and Unique to B — side by side */}
        {hasUniqueSection && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <AnalysisSection
              title={`${resolveTranslation(translateUniqueToSegment, analysisLanguage)} ${data.filter_a_name}`}
              icon={<ArrowLeftRight className="h-4 w-4" />}
              copyText={
                data.unique_to_a.length > 0
                  ? formatSectionsForClipboard(data.unique_to_a)
                  : undefined
              }
              defaultOpen
              fullWidth
              hasData={data.unique_to_a.length > 0}
              noDataText={`No themes unique to ${data.filter_a_name}.`}
            >
              <InsightCardList data={data.unique_to_a} />
            </AnalysisSection>

            <AnalysisSection
              title={`${resolveTranslation(translateUniqueToSegment, analysisLanguage)} ${data.filter_b_name}`}
              icon={<ArrowLeftRight className="h-4 w-4" />}
              copyText={
                data.unique_to_b.length > 0
                  ? formatSectionsForClipboard(data.unique_to_b)
                  : undefined
              }
              defaultOpen
              fullWidth
              hasData={data.unique_to_b.length > 0}
              noDataText={`No themes unique to ${data.filter_b_name}.`}
            >
              <InsightCardList data={data.unique_to_b} />
            </AnalysisSection>
          </div>
        )}

        {/* Per-Question Comparison — only show questions where the AI
            actually surfaced a meaningful difference. Questions whose
            key_differences AND shared_patterns are both empty are noise:
            the AI generated summaries for both segments but found nothing
            of substance to contrast, so they would clutter the report
            without adding insight. */}
        {(() => {
          const meaningfulQuestions = (data.per_question ?? []).filter(
            (q) => q.key_differences.length > 0 || q.shared_patterns.length > 0,
          );
          if (meaningfulQuestions.length === 0) return null;
          return (
            <AnalysisSection
              title={resolveTranslation(translatePerQuestionComparison, analysisLanguage)}
              icon={<HelpCircle className="h-5 w-5" />}
              fullWidth
              defaultOpen
            >
              <Accordion
                type="multiple"
                defaultValue={meaningfulQuestions.slice(0, 3).map((q) => q.question_id)}
              >
                {meaningfulQuestions.map((q) => (
                <AccordionItem key={q.question_id} value={q.question_id}>
                  <AccordionTrigger className="text-sm font-medium text-neutral-800">
                    {q.question_text}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-3">
                      {/* Summary A */}
                      <div>
                        <h4 className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-2">
                          {data.filter_a_name}
                        </h4>
                        <p className="text-sm text-neutral-700 leading-relaxed">{q.summary_a}</p>
                      </div>
                      {/* Summary B */}
                      <div>
                        <h4 className="text-xs font-semibold text-pink-500 uppercase tracking-wide mb-2">
                          {data.filter_b_name}
                        </h4>
                        <p className="text-sm text-neutral-700 leading-relaxed">{q.summary_b}</p>
                      </div>
                    </div>

                    {/* Key differences */}
                    {q.key_differences.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-2">
                          {resolveTranslation(translateKeyDifferences, analysisLanguage)}
                        </h4>
                        <InsightCardList data={q.key_differences} />
                      </div>
                    )}

                    {/* Shared patterns */}
                    {q.shared_patterns.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-2">
                          {resolveTranslation(translateSharedPatterns, analysisLanguage)}
                        </h4>
                        <InsightCardList data={q.shared_patterns} />
                      </div>
                    )}

                    {/* Category comparison */}
                    {filterIdA && filterIdB && (
                      <ComparisonCategoryChart
                        questionId={q.question_id}
                        questionText={q.question_text}
                        filterIdA={filterIdA}
                        filterIdB={filterIdB}
                        filterAName={data.filter_a_name}
                        filterBName={data.filter_b_name}
                      />
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
              </Accordion>
            </AnalysisSection>
          );
        })()}
      </div>
    </div>
  );
}

function ComparisonReport({ comparisonId, analysisLanguage }: ComparisonReportProps) {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const [, setSearchParams] = useSearchParams();
  const { data, isLoading, error } = useGetComparison(projectId, comparisonId);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          <p className="text-sm text-neutral-500">{lang.get('msg.loadingComparisonReport')}</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
          <AlertTriangle className="h-8 w-8 text-amber-500" />
          <p className="text-sm text-neutral-700">{lang.get('msg.comparisonNotFound')}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete(SearchParam.COMPARISON_ID);
                return next;
              });
            }}
          >
            {lang.get('msg.backToComparisons')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <ComparisonReportContent
      data={data.result}
      analysisLanguage={analysisLanguage}
      filterIdA={data.filter_id_a}
      filterIdB={data.filter_id_b}
    />
  );
}

export default ComparisonReport;
